import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/brand/StatusBadge";
import OpportunityDialog from "@/components/opportunities/OpportunityDialog";
import AppointmentDialog from "@/components/appointments/AppointmentDialog";
import QuoteDialog from "@/components/quotes/QuoteDialog";
import { Target, Loader2, Plus, Pencil, CalendarClock, CalendarPlus, FileText, User as UserIcon, MessageCircle } from "lucide-react";
import { stageTone, priorityTone, eur } from "@/lib/crm";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

export default function OpportunitiesPanel({ customerId, customerName, onChanged }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canManageAppt = !!user?.can_manage_appointments;
  const [rows, setRows] = useState(null);
  const [add, setAdd] = useState(false);
  const [edit, setEdit] = useState(null);
  const [appt, setAppt] = useState(null);
  const [quoteOpp, setQuoteOpp] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/customers/${customerId}/opportunities`); setRows(data); }
    catch (e) { setRows([]); }
  }, [customerId]);

  useEffect(() => { load(); }, [load]);
  const refresh = () => { load(); onChanged?.(); };

  return (
    <div className="space-y-4" data-testid="opportunities-panel">
      <div className="flex items-center justify-between">
        <h3 className="ds-section-title flex items-center gap-2"><Target className="h-4 w-4 text-brand" /> Opportunità · Efficienza</h3>
        <Button size="sm" onClick={() => setAdd(true)} className="gap-1.5" data-testid="add-opportunity-button"><Plus className="h-4 w-4" /> Nuova opportunità</Button>
      </div>

      {rows === null ? (
        <div className="py-10 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-brand" /></div>
      ) : rows.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-2 py-12 text-center text-slate-500">
          <Target className="h-8 w-8 text-slate-300" />
          <div className="font-display font-semibold text-slate-700">Nessuna opportunità</div>
          <p className="max-w-sm text-sm">Crea un'opportunità di efficienza (fotovoltaico, accumulo, pompe di calore…) per questo cliente.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((o) => (
            <div key={o.id} className="ds-card flex flex-wrap items-center justify-between gap-3 p-3" data-testid={`opp-item-${o.id}`}>
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold text-slate-800">{o.product}</span>
                  <StatusBadge type={stageTone(o.stage)}>{o.stage}</StatusBadge>
                  <StatusBadge type={priorityTone(o.priority)}>{o.priority}</StatusBadge>
                  <span className="text-sm text-slate-500">{eur(o.estimated_value)}</span>
                </div>
                <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-slate-500">
                  {o.next_action && !["Vinta", "Persa"].includes(o.stage) && <span className="inline-flex items-center gap-1 text-brand"><CalendarClock className="h-3 w-3" /> {o.next_action}{o.next_action_date ? ` · ${new Date(o.next_action_date).toLocaleDateString("it-IT")}` : ""}</span>}
                  {o.consultant_name && <span className="inline-flex items-center gap-1"><UserIcon className="h-3 w-3" /> {o.consultant_name}</span>}
                  {o.lost_reason && <span className="text-danger">Persa: {o.lost_reason}</span>}
                  {o.close_date && <span>Chiusa il {new Date(o.close_date).toLocaleDateString("it-IT")}</span>}
                </div>
              </div>
              {customerId && <Button size="sm" variant="ghost" onClick={() => navigate(`/whatsapp?customer=${customerId}`)} data-testid={`opp-whatsapp-${o.id}`} title="WhatsApp"><MessageCircle className="h-4 w-4 text-success" /></Button>}
              <Button size="sm" variant="ghost" onClick={() => setQuoteOpp(o)} data-testid={`opp-quote-${o.id}`} title="Crea preventivo"><FileText className="h-4 w-4" /></Button>
              {canManageAppt && <Button size="sm" variant="ghost" onClick={() => setAppt(o)} data-testid={`opp-appt-${o.id}`} title="Crea appuntamento"><CalendarPlus className="h-4 w-4" /></Button>}
              <Button size="sm" variant="ghost" onClick={() => setEdit(o)} data-testid={`edit-opp-${o.id}`}><Pencil className="h-4 w-4" /></Button>
            </div>
          ))}
        </div>
      )}

      <OpportunityDialog open={add} onOpenChange={setAdd} customerId={customerId} onSaved={refresh} />
      {edit && <OpportunityDialog open={!!edit} onOpenChange={(v) => !v && setEdit(null)} customerId={customerId} opportunity={edit} onSaved={() => { setEdit(null); refresh(); }} />}
      {appt && <AppointmentDialog open={!!appt} onOpenChange={(v) => !v && setAppt(null)} customerId={customerId} customerName={customerName} presetOpportunityId={appt.id} presetType="Sopralluogo" onSaved={() => { setAppt(null); onChanged?.(); }} />}
      {quoteOpp && <QuoteDialog open={!!quoteOpp} onOpenChange={(v) => !v && setQuoteOpp(null)} customerId={customerId} prefill={{ category: "Efficienza", product: quoteOpp.product, opportunity_id: quoteOpp.id }} onSaved={() => { setQuoteOpp(null); onChanged?.(); }} />}
    </div>
  );
}
